import features from "features";
import { handleGenericError } from "libs/error_handling";
import type { ModifierKeys, MouseBindingMap } from "libs/input";
import { V3 } from "libs/mjs";
import { clamp } from "libs/utils";
import { document } from "libs/window";
import { Color } from "three";
import { userSettings } from "types/schemas/user_settings.schema";
import {
  ContourModeEnum,
  type OrthoView,
  OrthoViews,
  type Point2,
  type Vector3,
  type Viewport,
} from "viewer/constants";
import {
  createBoundingBoxAndGetEdges,
  getClosestHoveredBoundingBox,
  handleMovingBoundingBox,
  handleResizingBoundingBox,
  highlightAndSetCursorOnHoveredBoundingBox,
  type SelectedEdge,
} from "viewer/controller/combinations/bounding_box_handlers";
import {
  handleMovePlane,
  handleOverViewport,
  moveW,
  moveWhenAltIsPressed,
  setMousePosition,
  zoom,
  zoomPlanes,
} from "viewer/controller/combinations/move_handlers";
import {
  handleAgglomerateTreeAtClick,
  handleClickSegment,
} from "viewer/controller/combinations/segmentation_handlers";
import {
  finishNodeMovement,
  handleCreateNodeFromEvent,
  handleDeleteEdge,
  handleMergeTrees,
  handleOpenContextMenu,
  handleSelectNode,
  maybeGetNodeIdFromPosition,
  moveNode,
} from "viewer/controller/combinations/skeleton_handlers";
import {
  changeBrushSizeIfBrushIsActiveBy,
  getSegmentIdForPosition,
  getUnmappedSegmentIdForPosition,
  handleDrawStart,
  handleEndForDrawOrErase,
  handleEraseStart,
  handleFloodFill,
  handleMoveForDrawOrErase,
  handlePickCell,
} from "viewer/controller/combinations/volume_handlers";
import getSceneController from "viewer/controller/scene_controller_provider";
import { isSkeletonLayerVisible } from "viewer/model/accessors/skeletontracing_accessor";
import {
  AnnotationTool,
  type AnnotationToolId,
  isBrushTool,
} from "viewer/model/accessors/tool_accessor";
import { getSomeTracing } from "viewer/model/accessors/tracing_accessor";
import { calculateGlobalPos } from "viewer/model/accessors/view_mode_accessor";
import {
  enforceActiveVolumeTracing,
  getActiveSegmentationTracing,
  getContourTracingMode,
  getMaximumBrushSize,
  getSegmentColorAsHSLA,
} from "viewer/model/accessors/volumetracing_accessor";
import { finishedResizingUserBoundingBoxAction } from "viewer/model/actions/annotation_actions";
import {
  minCutAgglomerateWithPositionAction,
  proofreadAtPosition,
  proofreadMergeAction,
  toggleSegmentInPartitionAction,
} from "viewer/model/actions/proofread_actions";
import { updateUserSettingAction } from "viewer/model/actions/settings_actions";
import {
  createBranchPointAction,
  createTreeAction,
  requestDeleteBranchPointAction,
} from "viewer/model/actions/skeletontracing_actions";
import { deleteNodeAsUserAction } from "viewer/model/actions/skeletontracing_actions_with_effects";
import {
  hideMeasurementTooltipAction,
  setActiveUserBoundingBoxId,
  setIsMeasuringAction,
  setLastMeasuredPositionAction,
  setQuickSelectStateAction,
  setVoxelPipetteTooltipPinnedPositionAction,
} from "viewer/model/actions/ui_actions";
import {
  computeQuickSelectForPointAction,
  computeQuickSelectForRectAction,
  confirmQuickSelectAction,
  createCellAction,
  hideBrushAction,
  interpolateSegmentationLayerAction,
} from "viewer/model/actions/volumetracing_actions";
import { reserveIdAndAddBoundingBox } from "viewer/model/helpers/bounding_box_creation_helpers";
import { api } from "viewer/singletons";
import Store, { type UserConfiguration } from "viewer/store";
import { getDefaultBrushSizes } from "viewer/view/action_bar/tools/brush_presets";
import type FlightModeView from "viewer/view/arbitrary_view";
import type { KeyboardShortcutHandlerMap } from "viewer/view/keyboard_shortcuts/keyboard_shortcut_types";
import { showToastWarningForLargestSegmentIdMissing } from "viewer/view/largest_segment_id_modal";
import type PlaneView from "viewer/view/plane_view";

export type ActionDescriptor = {
  leftClick?: string;
  rightClick: string;
  leftDrag?: string;
  rightDrag?: string;
};

/*
  This module contains classes for the different tools, such as MoveToolController, SkeletonToolController, DrawToolController etc.
  Each tool class defines getMouseControls which declares how mouse bindings are mapped (depending on
  modifiers) to actions. For the actions, code from webknossos/controller/combinations is called.

  If a tool does not define a specific mouse binding, the bindings of the MoveToolController are used as a fallback.
  See `createToolDependentMouseHandler` in plane_controller.js

  In general, each tool has to check the pressed modifiers and delegate to another tool if necessary.
  For example, the drawing tool delegates to the pick-cell-tool if shift is pressed.
  In other modules, we usually use `adaptActiveToolToShortcuts` to know which tool should be active
  when pressing a modifier, but in this module we keep explicit control over which tool is really active
  and delegate manually.

  Note that `getActionDescriptors` must not delegate to other tools. Instead, the semantic is always
  so that the returned hint of class X is only rendered if `adaptActiveToolToShortcuts` returns X.
  Therefore, the returned actions of a tool class should only refer to the actions of that tool class.
*/

abstract class ToolController {
  static getMouseControls(_planeId: OrthoView, _planeView: PlaneView): MouseBindingMap {
    return {};
  }
  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return { rightClick: "Context menu" };
  }
  static getKeyboardControls(): Partial<KeyboardShortcutHandlerMap> {
    return {};
  }

  static onToolDeselected() {}
}
export class MoveToolController extends ToolController {
  static getMouseControls(planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    return {
      scroll: (delta: number, type: ModifierKeys | null | undefined) => {
        switch (type) {
          case null: {
            moveW(delta, true, false, true);
            break;
          }

          case "alt":
          case "ctrlOrMeta": {
            zoomPlanes(clamp(-1, delta, 1), true);
            break;
          }

          case "shift": {
            const { uiInformation, annotation } = Store.getState();
            const isBrushActive = isBrushTool(uiInformation.activeTool);

            if (isBrushActive) {
              // Different browsers send different deltas, this way the behavior is comparable
              if (delta > 0) {
                changeBrushSizeIfBrushIsActiveBy(1);
              } else {
                changeBrushSizeIfBrushIsActiveBy(-1);
              }
            } else if (annotation.skeleton) {
              // Different browsers send different deltas, this way the behavior is comparable
              api.tracing.setNodeRadius(delta > 0 ? 5 : -5);
            }

            break;
          }

          default: // ignore other cases
        }
      },
      over: () => {
        handleOverViewport(planeId);
      },
      leftClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (event.shiftKey || !useLegacyBindings) {
          if (handleSelectNode(planeView, pos, plane, isTouch)) {
            return;
          }
          const clickedEdge = getClosestHoveredBoundingBox(pos, planeId);
          if (clickedEdge) {
            Store.dispatch(setActiveUserBoundingBoxId(clickedEdge[0].boxId));
            return;
          }
        }
        handleClickSegment(pos);
      },
      leftDoubleClick: (pos: Point2, _plane: OrthoView, _event: MouseEvent, _isTouch: boolean) => {
        const { uiInformation } = Store.getState();
        const isMoveToolActive = uiInformation.activeTool === AnnotationTool.MOVE;

        if (isMoveToolActive) {
          // We want to select the clicked segment ID only in the MOVE tool. This method is
          // implemented within the Move tool, but other tool controls will fall back to this one
          // if they didn't define the double click hook. However, for most other tools, this behavior
          // would be suboptimal, because when doing a double click, the first click will also be registered
          // as a simple left click. For example, doing a double click with the brush tool would brush something
          // and then immediately select the id again which is weird.
          handlePickCell(pos);
        }
      },
      middleClick: (pos: Point2, _plane: OrthoView, event: MouseEvent) => {
        if (event.shiftKey) {
          handleAgglomerateTreeAtClick(pos);
        }
      },
      pinch: (delta: number, center: Point2) => {
        setMousePosition(center);
        zoom(delta, true);
      },
      mouseMove: (delta: Point2, position: Point2, _id: any, event: MouseEvent) => {
        moveWhenAltIsPressed(delta, position, _id, event);
        if (planeId !== OrthoViews.TDView) {
          const hoveredEdgesInfo = getClosestHoveredBoundingBox(position, planeId);
          if (hoveredEdgesInfo) {
            const [primaryEdge] = hoveredEdgesInfo;
            getSceneController().highlightUserBoundingBox(primaryEdge.boxId);
          } else {
            getSceneController().highlightUserBoundingBox(null);
          }
        }
      },
      out: () => {
        setMousePosition(null);
      },
      leftDownMove: (
        delta: Point2,
        _pos: Point2,
        _id: string | null | undefined,
        _event: MouseEvent,
      ) => {
        handleMovePlane(delta);
      },
      middleDownMove: handleMovePlane,
      rightClick: MoveToolController.createRightClickHandler(planeView),
    };
  }

  static createRightClickHandler(planeView: PlaneView) {
    return (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) =>
      handleOpenContextMenu(planeView, pos, plane, isTouch, event);
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    userConfiguration: UserConfiguration,
    shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    const { useLegacyBindings } = userConfiguration;
    // In legacy mode, don't display a hint for
    // left click as it would be equal to left drag.
    // We also don't show a hint when the alt key was pressed,
    // as this mostly happens when the user presses alt in another tool
    // to move around while moving the mouse. In that case, clicking won't
    // select anything.
    const leftClickInfo =
      (useLegacyBindings && !shiftKey) || altKey
        ? {}
        : {
            leftClick: "Select Node",
          };
    return { ...leftClickInfo, leftDrag: "Move", rightClick: "Context Menu" };
  }
}

export class SkeletonToolController extends ToolController {
  static getMouseControls(_planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    const legacyRightClick = (
      position: Point2,
      plane: OrthoView,
      event: MouseEvent,
      isTouch: boolean,
    ) => {
      const { activeViewport } = Store.getState().viewModeData.plane;

      if (activeViewport === OrthoViews.TDView) {
        return;
      }

      if (event.shiftKey) {
        handleOpenContextMenu(planeView, position, plane, isTouch, event);
      } else {
        handleCreateNodeFromEvent(position, event.ctrlKey || event.metaKey);
      }
    };

    let draggingNodeId: number | null | undefined = null;
    let lastContinuouslyPlacedNodeTimestamp: number | null = null;
    let didDragNode: boolean = false;

    const maybePlaceContinuousNode = (pos: Point2, plane: string | null | undefined) => {
      if (
        lastContinuouslyPlacedNodeTimestamp &&
        Date.now() - lastContinuouslyPlacedNodeTimestamp < 200
      ) {
        return;
      }
      lastContinuouslyPlacedNodeTimestamp = Date.now();
      if (plane) {
        const globalPosition = calculateGlobalPos(Store.getState(), pos);
        api.tracing.createNode(globalPosition.rounded, { center: false });
      }
    };

    return {
      leftMouseDown: (pos: Point2, plane: OrthoView, _event: MouseEvent, isTouch: boolean) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (useLegacyBindings) {
          // There's no implicit node selection happening in the legacy mode
          return;
        }

        draggingNodeId = maybeGetNodeIdFromPosition(planeView, pos, plane, isTouch);
      },
      leftMouseUp: () => {
        if (draggingNodeId != null && didDragNode) {
          finishNodeMovement(draggingNodeId);
        }
        draggingNodeId = null;
        didDragNode = false;
      },
      leftDownMove: (
        delta: Point2,
        pos: Point2,
        plane: string | null | undefined,
        event: MouseEvent,
      ) => {
        const { annotation, userConfiguration } = Store.getState();
        const { useLegacyBindings, continuousNodeCreation } = userConfiguration;

        if (continuousNodeCreation && !useLegacyBindings) {
          maybePlaceContinuousNode(pos, plane);
        } else {
          if (
            annotation.skeleton != null &&
            (draggingNodeId != null || (useLegacyBindings && (event.ctrlKey || event.metaKey)))
          ) {
            didDragNode = true;
            moveNode(delta.x, delta.y, draggingNodeId, true);
          } else {
            handleMovePlane(delta);
          }
        }
      },
      rightDownMove: (
        _delta: Point2,
        pos: Point2,
        plane: string | null | undefined,
        _event: MouseEvent,
      ) => {
        const { userConfiguration } = Store.getState();
        const { useLegacyBindings, continuousNodeCreation } = userConfiguration;

        if (!useLegacyBindings || !continuousNodeCreation) {
          return;
        }

        maybePlaceContinuousNode(pos, plane);
      },
      leftClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        this.onLeftClick(
          planeView,
          pos,
          event.shiftKey,
          event.altKey,
          event.ctrlKey || event.metaKey,
          plane,
          isTouch,
        );
      },
      rightClick: (position: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (useLegacyBindings) {
          legacyRightClick(position, plane, event, isTouch);
          return;
        }

        handleOpenContextMenu(planeView, position, plane, isTouch, event);
      },
    };
  }

  static onLeftClick(
    planeView: PlaneView | FlightModeView,
    position: Point2,
    shiftPressed: boolean,
    altPressed: boolean,
    ctrlPressed: boolean,
    plane: Viewport,
    isTouch: boolean,
    allowNodeCreation: boolean = true,
  ): void {
    const { useLegacyBindings, continuousNodeCreation } = Store.getState().userConfiguration;
    const showSkeleton = isSkeletonLayerVisible(Store.getState());
    if (!showSkeleton) {
      // Don't do anything in case the skeleton layer is disabled or does not exist.
      return;
    }

    if (continuousNodeCreation && allowNodeCreation && !useLegacyBindings) {
      handleCreateNodeFromEvent(position, ctrlPressed);
      return;
    }

    // The following functions are all covered by the context menu, too.
    // (At least, in the XY/XZ/YZ viewports).
    if (shiftPressed && altPressed) {
      handleMergeTrees(planeView, position, plane, isTouch);
      return;
    } else if (shiftPressed && ctrlPressed) {
      handleDeleteEdge(planeView, position, plane, isTouch);
      return;
    }

    let didSelectNode;
    if (shiftPressed || !useLegacyBindings) {
      didSelectNode = handleSelectNode(planeView, position, plane, isTouch);
    }

    if (allowNodeCreation && !didSelectNode && !useLegacyBindings && !shiftPressed) {
      // Will only have an effect, when not in 3D viewport
      handleCreateNodeFromEvent(position, ctrlPressed);
    }
  }

  static getKeyboardControls(): Partial<KeyboardShortcutHandlerMap> {
    return {
      // Delete active node
      DELETE_ACTIVE_NODE_PLANE: {
        onPressed: () => {
          Store.dispatch(deleteNodeAsUserAction(Store.getState()));
        },
      },
      CREATE_TREE_PLANE: {
        onPressed: () => {
          Store.dispatch(createTreeAction());
        },
      },
      // Branches
      CREATE_BRANCH_POINT_PLANE: {
        onPressed: () => {
          Store.dispatch(createBranchPointAction());
        },
      },
      DELETE_BRANCH_POINT_PLANE: {
        onPressed: () => {
          Store.dispatch(requestDeleteBranchPointAction());
        },
      },
      MOVE_NODE_LEFT: {
        onPressedWithRepeat: () => moveNode(-1, 0),
        delayed: true,
      },
      MOVE_NODE_RIGHT: {
        onPressedWithRepeat: () => moveNode(1, 0),
        delayed: true,
      },
      MOVE_NODE_UP: {
        onPressedWithRepeat: () => moveNode(0, -1),
        delayed: true,
      },
      MOVE_NODE_DOWN: {
        onPressedWithRepeat: () => moveNode(0, 1),
        delayed: true,
      },
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    userConfiguration: UserConfiguration,
    shiftKey: boolean,
    ctrlOrMetaKey: boolean,
    altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    const { continuousNodeCreation } = Store.getState().userConfiguration;
    const { useLegacyBindings } = userConfiguration;
    if (continuousNodeCreation) {
      if (useLegacyBindings) {
        return {
          leftDrag: "Move",
          rightClick: "Place node",
          rightDrag: "Draw nodes",
        };
      }
      return {
        leftClick: "Place node",
        leftDrag: "Draw nodes",
        rightClick: "Context Menu",
      };
    }

    // In legacy mode, don't display a hint for
    // left click as it would be equal to left drag
    let leftClickInfo = {};
    if (shiftKey && altKey) {
      leftClickInfo = {
        leftClick: "Create edge between nodes",
      };
    } else if (shiftKey && ctrlOrMetaKey) {
      leftClickInfo = {
        leftClick: "Delete edge between nodes",
      };
    } else if (shiftKey) {
      leftClickInfo = {
        leftClick: "Select node",
      };
    } else if (!useLegacyBindings && ctrlOrMetaKey && !shiftKey && !altKey) {
      leftClickInfo = {
        leftClick: "Place Node without Activating",
      };
    } else if (!useLegacyBindings && !ctrlOrMetaKey && !shiftKey && !altKey) {
      leftClickInfo = {
        leftClick: "Place/Select Node",
      };
    }

    return {
      ...leftClickInfo,
      leftDrag: "Move",
      rightClick: useLegacyBindings && !shiftKey ? "Place Node" : "Context Menu",
    };
  }

  static onToolDeselected() {}
}

class VolumeToolController extends ToolController {
  static getKeyboardControls(): Partial<KeyboardShortcutHandlerMap> {
    return {
      CREATE_NEW_CELL: {
        onPressed: () => {
          const volumeTracing = getActiveSegmentationTracing(Store.getState());

          if (volumeTracing == null || volumeTracing.tracingId == null) {
            return;
          }

          if (volumeTracing.largestSegmentId != null) {
            Store.dispatch(
              createCellAction(volumeTracing.activeCellId, volumeTracing.largestSegmentId),
            );
          } else {
            showToastWarningForLargestSegmentIdMissing(volumeTracing);
          }
        },
      },
      INTERPOLATE_SEGMENTATION: {
        onPressed: () => {
          Store.dispatch(interpolateSegmentationLayerAction());
        },
      },
      BRUSH_PRESET_SMALL: {
        onPressed: () => {
          let brushPresets = Store.getState().userConfiguration.presetBrushSizes;
          if (brushPresets == null) {
            const maximumBrushSize = getMaximumBrushSize(Store.getState());
            brushPresets = getDefaultBrushSizes(maximumBrushSize, userSettings.brushSize.minimum);
            Store.dispatch(updateUserSettingAction("presetBrushSizes", brushPresets));
          }
          Store.dispatch(updateUserSettingAction("brushSize", brushPresets.small));
        },
      },
      BRUSH_PRESET_MEDIUM: {
        onPressed: () => {
          let brushPresets = Store.getState().userConfiguration.presetBrushSizes;
          if (brushPresets == null) {
            const maximumBrushSize = getMaximumBrushSize(Store.getState());
            brushPresets = getDefaultBrushSizes(maximumBrushSize, userSettings.brushSize.minimum);
            Store.dispatch(updateUserSettingAction("presetBrushSizes", brushPresets));
          }
          Store.dispatch(updateUserSettingAction("brushSize", brushPresets.medium));
        },
      },
      BRUSH_PRESET_LARGE: {
        onPressed: () => {
          let brushPresets = Store.getState().userConfiguration.presetBrushSizes;
          if (brushPresets == null) {
            const maximumBrushSize = getMaximumBrushSize(Store.getState());
            brushPresets = getDefaultBrushSizes(maximumBrushSize, userSettings.brushSize.minimum);
            Store.dispatch(updateUserSettingAction("presetBrushSizes", brushPresets));
          }
          Store.dispatch(updateUserSettingAction("brushSize", brushPresets.large));
        },
      },
      DECREASE_BRUSH_SIZE: {
        onPressedWithRepeat: () => changeBrushSizeIfBrushIsActiveBy(-1),
        delayed: true,
      },
      INCREASE_BRUSH_SIZE: {
        onPressedWithRepeat: () => changeBrushSizeIfBrushIsActiveBy(1),
        delayed: true,
      },
    };
  }
}
export class DrawToolController extends VolumeToolController {
  static getPlaneMouseControls(_planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    return {
      leftDownMove: (_delta: Point2, pos: Point2) => {
        handleMoveForDrawOrErase(pos);
      },
      leftMouseDown: (pos: Point2, plane: OrthoView, event: MouseEvent) => {
        const ctrlOrMetaPressed = event.ctrlKey || event.metaKey;
        if (event.shiftKey && !ctrlOrMetaPressed) {
          // Should select cell. Do nothing, since case is covered by leftClick.
          return;
        }

        if (ctrlOrMetaPressed && event.shiftKey) {
          handleEraseStart(pos, plane);
          return;
        }

        handleDrawStart(pos, plane);
      },
      leftMouseUp: () => {
        handleEndForDrawOrErase();
      },
      rightDownMove: (_delta: Point2, pos: Point2) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (!useLegacyBindings) {
          return;
        }

        const state = Store.getState();
        const volumeTracing = enforceActiveVolumeTracing(state);
        const contourTracingMode = getContourTracingMode(state, volumeTracing);

        if (contourTracingMode === ContourModeEnum.DELETE) {
          handleMoveForDrawOrErase(pos);
        }
      },
      rightMouseDown: (pos: Point2, plane: OrthoView, event: MouseEvent) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (!useLegacyBindings) {
          return;
        }

        if (!event.shiftKey) {
          handleEraseStart(pos, plane);
        }
      },
      rightMouseUp: () => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (!useLegacyBindings) {
          return;
        }

        handleEndForDrawOrErase();
      },
      leftClick: (pos: Point2, _plane: OrthoView, event: MouseEvent) => {
        const ctrlOrMetaPressed = event.ctrlKey || event.metaKey;
        const shouldPickCell = event.shiftKey && !ctrlOrMetaPressed;
        const shouldErase = event.shiftKey && ctrlOrMetaPressed;

        if (shouldPickCell) {
          handlePickCell(pos);
        } else if (shouldErase) {
          // Do nothing. This case is covered by leftMouseDown.
        }
      },
      rightClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        const { useLegacyBindings } = Store.getState().userConfiguration;

        if (useLegacyBindings) {
          // Don't do anything. rightMouse* will take care of brushing.
          return;
        }

        handleOpenContextMenu(planeView, pos, plane, isTouch, event);
      },
      out: () => {
        Store.dispatch(hideBrushAction());
      },
    };
  }

  static getActionDescriptors(
    activeTool: AnnotationTool,
    userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    let rightClick;
    const { useLegacyBindings } = userConfiguration;

    if (!useLegacyBindings) {
      rightClick = "Context Menu";
    } else {
      rightClick = `Erase (${activeTool === AnnotationTool.BRUSH ? "Brush" : "Trace"})`;
    }

    return {
      leftDrag: activeTool === AnnotationTool.BRUSH ? "Brush" : "Trace",
      rightClick,
    };
  }

  static onToolDeselected() {}
}
export class EraseToolController extends VolumeToolController {
  static getPlaneMouseControls(_planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    return {
      leftDownMove: (_delta: Point2, pos: Point2) => {
        handleMoveForDrawOrErase(pos);
      },
      leftMouseDown: (pos: Point2, plane: OrthoView, event: MouseEvent) => {
        if (event.shiftKey || event.ctrlKey || event.metaKey) {
          return;
        }

        handleEraseStart(pos, plane);
      },
      leftMouseUp: () => {
        handleEndForDrawOrErase();
      },
      leftClick: (pos: Point2, plane: OrthoView, event: MouseEvent) => {
        const isControlOrMetaPressed = event.ctrlKey || event.metaKey;
        if (event.shiftKey) {
          if (isControlOrMetaPressed) {
            handleFloodFill(Store.getState(), pos, plane);
          } else {
            handlePickCell(pos);
          }
        }
      },
      rightClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        handleOpenContextMenu(planeView, pos, plane, isTouch, event);
      },
      out: () => {
        Store.dispatch(hideBrushAction());
      },
    };
  }

  static getActionDescriptors(
    activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftDrag: `Erase (${activeTool === AnnotationTool.ERASE_BRUSH ? "Brush" : "Trace"})`,
      rightClick: "Context Menu",
    };
  }

  static onToolDeselected() {}
}

// Not inheriting from VolumeToolController by design as no shortcuts like
// `c` -> create new cell should be supported in this tool for now.
export class VoxelPipetteToolController extends ToolController {
  static getPlaneMouseControls(_planeId: OrthoView): MouseBindingMap {
    return {
      mouseMove: (
        _delta: Point2,
        position: Point2,
        plane: OrthoView | null | undefined,
        event: MouseEvent,
      ) => {
        moveWhenAltIsPressed(_delta, position, plane, event);
      },
      leftClick: (position: Point2, plane: OrthoView, event: MouseEvent) => {
        if (event.shiftKey) {
          handlePickCell(position);
          return;
        }

        const state = Store.getState();
        const { pinnedPosition } = state.uiInformation.voxelPipetteToolInfo;

        if (pinnedPosition == null) {
          const globalPosition = calculateGlobalPos(state, position, plane).floating;
          Store.dispatch(setVoxelPipetteTooltipPinnedPositionAction(globalPosition));
        } else {
          Store.dispatch(setVoxelPipetteTooltipPinnedPositionAction(null));
        }
      },
    };
  }

  static onToolDeselected() {
    Store.dispatch(setVoxelPipetteTooltipPinnedPositionAction(null));
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftClick: !shiftKey ? "Pin Tooltip" : "Activate Segment ID",
      rightClick: "Context Menu",
    };
  }
}
export class FillCellToolController extends VolumeToolController {
  static getPlaneMouseControls(_planeId: OrthoView): MouseBindingMap {
    return {
      leftClick: (pos: Point2, plane: OrthoView, event: MouseEvent) => {
        const shouldPickCell = event.shiftKey && !(event.ctrlKey || event.metaKey);

        if (shouldPickCell) {
          handlePickCell(pos);
        } else {
          handleFloodFill(Store.getState(), pos, plane);
        }
      },
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftClick: "Fill Segment",
      rightClick: "Context Menu",
    };
  }

  static onToolDeselected() {}
}

export class BoundingBoxToolController extends ToolController {
  static getPlaneMouseControls(planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    let primarySelectedEdge: SelectedEdge | null | undefined = null;
    let secondarySelectedEdge: SelectedEdge | null | undefined = null;
    // Accumulator for fractional movement that gets lost to rounding
    let movementAccumulator: Vector3 = [0, 0, 0];
    // True while a new bounding box's id is being reserved asynchronously (see
    // createBoundingBoxAndGetEdges). Without this, leftDownMove would fall through to
    // panning the view for the duration of the reservation, since primarySelectedEdge
    // is not set yet at that point.
    let isCreatingBoundingBox = false;
    // Tracks the mouse position while isCreatingBoundingBox is true, so the box can be
    // resized to reflect any dragging that happened while its id was still being reserved
    // (otherwise it would be stuck at its initial 1x1x1 size).
    let latestPosDuringCreation: Point2 | null = null;
    // Whether the left mouse button is still held down. A quick click can release it
    // before the async id reservation resolves.
    let isMouseStillDown = false;
    return {
      leftDownMove: (
        delta: Point2,
        pos: Point2,
        _id: string | null | undefined,
        event: MouseEvent,
      ) => {
        if (isCreatingBoundingBox) {
          latestPosDuringCreation = pos;
          return;
        }
        if (primarySelectedEdge == null) {
          handleMovePlane(delta);
          return;
        }
        if (event.ctrlKey || event.metaKey) {
          movementAccumulator = handleMovingBoundingBox(
            delta,
            planeId,
            primarySelectedEdge,
            movementAccumulator,
          );
        } else {
          handleResizingBoundingBox(pos, planeId, primarySelectedEdge, secondarySelectedEdge);
        }
      },
      leftMouseDown: async (pos: Point2, _plane: OrthoView, _event: MouseEvent) => {
        isMouseStillDown = true;
        if (isCreatingBoundingBox) {
          // A previous leftMouseDown is still awaiting its id reservation. Since mouse
          // events aren't serialized by the input framework, ignore this reentrant call
          // instead of starting a second, concurrent bounding box creation.
          latestPosDuringCreation = pos;
          return;
        }
        let hoveredEdgesInfo = getClosestHoveredBoundingBox(pos, planeId);

        if (hoveredEdgesInfo) {
          [primarySelectedEdge, secondarySelectedEdge] = hoveredEdgesInfo;
        } else {
          isCreatingBoundingBox = true;
          latestPosDuringCreation = pos;
          try {
            hoveredEdgesInfo = await createBoundingBoxAndGetEdges(pos, planeId);
          } finally {
            isCreatingBoundingBox = false;
          }
          if (hoveredEdgesInfo) {
            [primarySelectedEdge, secondarySelectedEdge] = hoveredEdgesInfo;
            if (latestPosDuringCreation != null) {
              // Catch up on any dragging that happened while the id was being reserved.
              handleResizingBoundingBox(
                latestPosDuringCreation,
                planeId,
                primarySelectedEdge,
                secondarySelectedEdge,
              );
            }
            if (!isMouseStillDown) {
              // The mouse was already released before the box was created (e.g. a quick
              // click). Finish the resize immediately instead of leaving it in an
              // active-drag state that would only be cleaned up on the next mouse-up.
              Store.dispatch(finishedResizingUserBoundingBoxAction(primarySelectedEdge.boxId));
              primarySelectedEdge = null;
              secondarySelectedEdge = null;
            }
          } else {
            isCreatingBoundingBox = false;
          }
        }
        if (primarySelectedEdge) {
          getSceneController().highlightUserBoundingBox(primarySelectedEdge.boxId);
        }
      },
      leftMouseUp: () => {
        isMouseStillDown = false;
        if (primarySelectedEdge) {
          Store.dispatch(finishedResizingUserBoundingBoxAction(primarySelectedEdge.boxId));
        }
        movementAccumulator = [0, 0, 0];
        primarySelectedEdge = null;
        secondarySelectedEdge = null;
        getSceneController().highlightUserBoundingBox(null);
      },
      mouseMove: (delta: Point2, position: Point2, _id: any, event: MouseEvent) => {
        if (primarySelectedEdge == null && planeId !== OrthoViews.TDView) {
          moveWhenAltIsPressed(delta, position, _id, event);
          highlightAndSetCursorOnHoveredBoundingBox(position, planeId, event);
        }
      },
      leftClick: (pos: Point2, _plane: OrthoView, _event: MouseEvent) => {
        const currentlyHoveredEdge = getClosestHoveredBoundingBox(pos, planeId);
        if (currentlyHoveredEdge) {
          Store.dispatch(setActiveUserBoundingBoxId(currentlyHoveredEdge[0].boxId));
        }
      },
      rightClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        handleOpenContextMenu(planeView, pos, plane, isTouch, event);
      },
    };
  }

  static getKeyboardControls(): Partial<KeyboardShortcutHandlerMap> {
    const handleBBoxMoveModifierPressed = (event: KeyboardEvent) => {
      const { viewModeData, temporaryConfiguration } = Store.getState();
      const { mousePosition } = temporaryConfiguration;
      if (mousePosition == null) return;
      highlightAndSetCursorOnHoveredBoundingBox(
        { x: mousePosition[0], y: mousePosition[1] },
        viewModeData.plane.activeViewport,
        event,
      );
    };
    return {
      CREATE_BOUNDING_BOX: {
        onPressed: async () => {
          try {
            const tracingId = getSomeTracing(Store.getState().annotation).tracingId;
            // Queued behind any active rebase (see reserveIdAndAddBoundingBox).
            await reserveIdAndAddBoundingBox(Store.dispatch, tracingId);
          } catch (error) {
            handleGenericError(error as Error, "Could not create a new bounding box.");
          }
        },
      },
      TOGGLE_CURSOR_STATE_FOR_MOVING: {
        onPressed: handleBBoxMoveModifierPressed,
        onReleased: handleBBoxMoveModifierPressed,
      },
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftDrag: ctrlOrMetaKey ? "Move Bounding Boxes" : "Create/Resize Bounding Boxes",
      rightClick: "Context Menu",
    };
  }

  static onToolDeselected() {
    const { body } = document;

    if (body == null) {
      return;
    }
    getSceneController().highlightUserBoundingBox(null);
  }
}

export class QuickSelectToolController extends VolumeToolController {
  static getPlaneMouseControls(_planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    let startPos: Vector3 | null = null;
    let currentPos: Vector3 | null = null;
    let isDragging = false;
    const SceneController = getSceneController();
    const { quickSelectGeometry } = SceneController;
    return {
      leftMouseDown: (pos: Point2, _plane: OrthoView, _event: MouseEvent) => {
        // Potentially confirm earlier quick select actions. That way, the user
        // can draw multiple rectangles even in preview mode. When starting a new
        // rectangle, the old one is confirmed. If no quick select rectangle exists,
        // this is a noop effectively.
        Store.dispatch(confirmQuickSelectAction());
        quickSelectGeometry.detachTextureMask();

        Store.dispatch(setQuickSelectStateAction("drawing"));

        const state = Store.getState();
        quickSelectGeometry.rotateToViewport();
        // Only show the center marker when using the heuristic
        // approach since the center will be used as a seed for the "floodfill".
        // For the ML-based approach, the center marker doesn't have any relevance.
        quickSelectGeometry.setCenterMarkerVisibility(
          Store.getState().userConfiguration.quickSelect.useHeuristic,
        );

        const volumeTracing = getActiveSegmentationTracing(state);
        if (!volumeTracing) {
          return;
        }

        const [h, s, l] = getSegmentColorAsHSLA(state, volumeTracing.activeCellId);
        const activeCellColor = new Color().setHSL(h, s, l);
        quickSelectGeometry.setColor(activeCellColor);
        startPos = calculateGlobalPos(state, pos).rounded;
        currentPos = startPos;
        isDragging = true;
      },
      leftMouseUp: () => {
        isDragging = false;
        // Identity equality is enough, since we want to catch the case
        // in which the user didn't move the mouse at all
        if (
          startPos === currentPos ||
          Store.getState().uiInformation.quickSelectState === "inactive"
        ) {
          // clear rectangle because user didn't drag
          return;
        }
        if (startPos != null && currentPos != null) {
          Store.dispatch(
            computeQuickSelectForRectAction(startPos, currentPos, quickSelectGeometry),
          );
        }
      },
      leftDownMove: (
        _delta: Point2,
        pos: Point2,
        _id: string | null | undefined,
        event: MouseEvent,
      ) => {
        if (
          !isDragging ||
          startPos == null ||
          Store.getState().uiInformation.quickSelectState === "inactive"
        ) {
          return;
        }
        const newCurrentPos = calculateGlobalPos(Store.getState(), pos).rounded;
        if (event.shiftKey) {
          // If shift is held, the rectangle is resized on topLeft and bottomRight
          // so that the center is constant.
          // We don't use the passed _delta variable, because that is given in pixel-space
          // instead of the physical space.
          if (currentPos) {
            const delta3D = V3.sub(newCurrentPos, currentPos);
            // Pseudo: startPos -= delta3D;
            V3.sub(startPos, delta3D, startPos);
          }
        }

        currentPos = newCurrentPos;

        quickSelectGeometry.setCoordinates(startPos, currentPos);
      },
      leftClick: (pos: Point2, _plane: OrthoView, _event: MouseEvent, _isTouch: boolean) => {
        const state = Store.getState();
        const clickedPos = calculateGlobalPos(state, pos).rounded;
        isDragging = false;

        const quickSelectConfig = state.userConfiguration.quickSelect;
        const isAISelectAvailable = features().segmentAnythingEnabled;
        const isQuickSelectHeuristic = quickSelectConfig.useHeuristic || !isAISelectAvailable;

        if (!isQuickSelectHeuristic) {
          Store.dispatch(computeQuickSelectForPointAction(clickedPos, quickSelectGeometry));
        }
      },
      rightClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        handleOpenContextMenu(planeView, pos, plane, isTouch, event);
      },
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftDrag: shiftKey ? "Resize Rectangle symmetrically" : "Draw Rectangle around Segment",
      rightClick: "Context Menu",
    };
  }

  static onToolDeselected() {}
}

function getDoubleClickGuard() {
  const DOUBLE_CLICK_TIME_THRESHOLD = 600;
  const MAX_MOUSE_MOVEMENT_FOR_DOUBLE_CLICK = 6;
  let lastLeftClickTime = 0;
  let lastClickPosition: Point2 = { x: -100, y: -100 };
  const doubleClickGuard = (pos: Point2, onDoubleClick: () => void) => {
    const currentTime = Date.now();
    if (
      currentTime - lastLeftClickTime <= DOUBLE_CLICK_TIME_THRESHOLD &&
      Math.abs(pos.x - lastClickPosition.x) + Math.abs(pos.y - lastClickPosition.y) <
        MAX_MOUSE_MOVEMENT_FOR_DOUBLE_CLICK
    ) {
      // A double click should also terminate measuring.
      onDoubleClick();
      return true;
    }
    lastLeftClickTime = currentTime;
    lastClickPosition = pos;
    return false;
  };
  return doubleClickGuard;
}

export class LineMeasurementToolController extends ToolController {
  static initialPlane: OrthoView = OrthoViews.PLANE_XY;
  static isMeasuring = false;
  static getPlaneMouseControls(): MouseBindingMap {
    const doubleClickGuard = getDoubleClickGuard();
    const SceneController = getSceneController();
    const { lineMeasurementGeometry } = SceneController;
    const mouseMove = (
      _delta: Point2,
      pos: Point2,
      plane: OrthoView | null | undefined,
      evt: MouseEvent,
    ) => {
      // In case the tool was reset by the user, abort measuring.
      if (lineMeasurementGeometry.wasReset && this.isMeasuring) {
        this.isMeasuring = false;
        Store.dispatch(setIsMeasuringAction(false));
      }
      const isAltPressed = evt.altKey;
      if (isAltPressed || plane !== this.initialPlane || !this.isMeasuring) {
        moveWhenAltIsPressed(_delta, pos, plane, evt);
        return;
      }
      const state = Store.getState();
      const newPos = calculateGlobalPos(state, pos, this.initialPlane).floating;
      lineMeasurementGeometry.updateLatestPointPosition(newPos);
      Store.dispatch(setLastMeasuredPositionAction(newPos));
    };
    const rightClick = (pos: Point2, plane: OrthoView, event: MouseEvent) => {
      // In case the tool was reset by the user, abort measuring.
      if (lineMeasurementGeometry.wasReset && this.isMeasuring) {
        this.isMeasuring = false;
        Store.dispatch(setIsMeasuringAction(false));
        return;
      }
      if (plane !== this.initialPlane) {
        return;
      }
      if (this.isMeasuring) {
        // Set the last point of the measurement and stop measuring.
        mouseMove({ x: 0, y: 0 }, pos, plane, event);
        this.isMeasuring = false;
        Store.dispatch(setIsMeasuringAction(false));
      } else {
        // If the tool already stopped measuring, reset the tool.
        lineMeasurementGeometry.resetAndHide();
        Store.dispatch(hideMeasurementTooltipAction());
      }
    };
    const leftClick = (pos: Point2, plane: OrthoView, event: MouseEvent) => {
      // In case the tool was reset by the user, abort measuring.
      if (lineMeasurementGeometry.wasReset && this.isMeasuring) {
        this.isMeasuring = false;
        Store.dispatch(setIsMeasuringAction(false));
        return;
      }
      if (
        doubleClickGuard(pos, () => {
          rightClick(pos, plane, event);
        }) ||
        (this.isMeasuring && plane !== this.initialPlane)
      ) {
        return;
      }
      // Set a new measurement point.
      const state = Store.getState();
      const position = calculateGlobalPos(state, pos, plane).floating;
      if (!this.isMeasuring) {
        this.initialPlane = plane;
        lineMeasurementGeometry.setStartPoint(position, plane);
        this.isMeasuring = true;
        Store.dispatch(setIsMeasuringAction(true));
      } else {
        lineMeasurementGeometry.addPoint(position);
      }
      Store.dispatch(setLastMeasuredPositionAction(position));
    };
    return {
      mouseMove,
      rightClick,
      leftClick,
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftClick: "Left Click to measure distance",
      rightClick: "Finish Measurement",
    };
  }

  static onToolDeselected() {
    const { lineMeasurementGeometry } = getSceneController();
    lineMeasurementGeometry.reset();
    lineMeasurementGeometry.hide();
    Store.dispatch(hideMeasurementTooltipAction());
    this.isMeasuring = false;
    this.initialPlane = OrthoViews.PLANE_XY;
  }
}

export class AreaMeasurementToolController extends ToolController {
  static initialPlane: OrthoView = OrthoViews.PLANE_XY;
  static isMeasuring = false;
  static getPlaneMouseControls(): MouseBindingMap {
    const SceneController = getSceneController();
    const { areaMeasurementGeometry } = SceneController;
    const doubleClickGuard = getDoubleClickGuard();
    const onRightClick = () => {
      areaMeasurementGeometry.reset();
      Store.dispatch(hideMeasurementTooltipAction());
    };
    return {
      leftDownMove: (
        _delta: Point2,
        pos: Point2,
        id: string | null | undefined,
        evt: MouseEvent,
      ) => {
        if (evt.altKey) {
          moveWhenAltIsPressed(_delta, pos, id, evt);
          return;
        }
        if (id == null) {
          return;
        }
        if (!this.isMeasuring) {
          this.initialPlane = id as OrthoView;
          this.isMeasuring = true;
          areaMeasurementGeometry.reset();
          areaMeasurementGeometry.show();
          areaMeasurementGeometry.setViewport(id as OrthoView);
        }
        if (id !== this.initialPlane) {
          return;
        }
        const state = Store.getState();
        const position = calculateGlobalPos(state, pos, this.initialPlane).floating;
        areaMeasurementGeometry.addEdgePoint(position);
        Store.dispatch(setLastMeasuredPositionAction(position));
      },
      leftMouseUp: () => {
        if (!this.isMeasuring) {
          return;
        }
        // Stop drawing area and close the drawn area if still measuring.
        this.isMeasuring = false;
        areaMeasurementGeometry.connectToStartPoint();
      },
      rightClick: onRightClick,
      leftClick: (pos: Point2) => {
        doubleClickGuard(pos, onRightClick);
      },
    };
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    _userConfiguration: UserConfiguration,
    _shiftKey: boolean,
    _ctrlOrMetaKey: boolean,
    _altKey: boolean,
    _isTDViewportActive: boolean,
  ): ActionDescriptor {
    return {
      leftDrag: "Drag to measure area",
      rightClick: "Reset Measurement",
    };
  }

  static onToolDeselected() {
    const { areaMeasurementGeometry } = getSceneController();
    areaMeasurementGeometry.resetAndHide();
    Store.dispatch(hideMeasurementTooltipAction());
    this.isMeasuring = false;
    this.initialPlane = OrthoViews.PLANE_XY;
  }
}

export class ProofreadToolController extends ToolController {
  static getPlaneMouseControls(_planeId: OrthoView, planeView: PlaneView): MouseBindingMap {
    return {
      leftClick: (pos: Point2, plane: OrthoView, event: MouseEvent, isTouch: boolean) => {
        this.onLeftClick(planeView, pos, plane, event, isTouch);
      },
    };
  }

  static getKeyboardControls(): Partial<KeyboardShortcutHandlerMap> {
    return {
      TOGGLE_MULTICUT_MODE: {
        onPressed: () => {
          const state = Store.getState();
          // Do not trigger in case proofreading is active as this is colliding with the default shortcut for multi split mode activation.
          // This is currently the only allowed shortcut collision manually resolved here. c.f.:  acceptedCollisions in keyboard_shortcut_utils.tsx.
          const isProofreadingActive = state.uiInformation.activeTool === AnnotationTool.PROOFREAD;
          if (isProofreadingActive) {
            const isMultiSplitActive = state.userConfiguration.isMultiSplitActive;
            Store.dispatch(updateUserSettingAction("isMultiSplitActive", !isMultiSplitActive));
          }
        },
      },
    };
  }

  static onLeftClick(
    _planeView: PlaneView,
    pos: Point2,
    plane: OrthoView,
    event: MouseEvent,
    _isTouch: boolean,
  ) {
    if (plane === OrthoViews.TDView) {
      // The click position cannot be mapped to a 3D coordinate in the
      // 3D viewport.
      return;
    }

    const state = Store.getState();
    const globalPosition = calculateGlobalPos(state, pos).rounded;
    const isMultiSplitActive = state.userConfiguration.isMultiSplitActive;
    const ctrlOrMetaKey = event.ctrlKey || event.metaKey;
    if (isMultiSplitActive && ctrlOrMetaKey) {
      const unmappedSegmentId = getUnmappedSegmentIdForPosition(globalPosition);
      const mappedSegmentId = getSegmentIdForPosition(globalPosition);
      if (unmappedSegmentId === 0n || mappedSegmentId === 0n) {
        // No valid ids were found, ignore action.
        return;
      }
      const partition = event.shiftKey ? "partitionB" : "partitionA";
      Store.dispatch(toggleSegmentInPartitionAction(unmappedSegmentId, partition, mappedSegmentId));
      return;
    }

    if (event.shiftKey) {
      Store.dispatch(proofreadMergeAction(globalPosition));
    } else if (event.ctrlKey || event.metaKey) {
      Store.dispatch(minCutAgglomerateWithPositionAction(globalPosition));
    } else {
      handlePickCell(pos);
      Store.dispatch(
        proofreadAtPosition(globalPosition, state.flycam.additionalCoordinates || undefined),
      );
    }
  }

  static getActionDescriptors(
    _activeTool: AnnotationTool,
    userConfiguration: UserConfiguration,
    shiftKey: boolean,
    ctrlOrMetaKey: boolean,
    _altKey: boolean,
    isTDViewportActive: boolean,
  ): ActionDescriptor {
    const { isMultiSplitActive } = userConfiguration;

    // --- Multi-split additions -----------------------------------------------
    const multiSplitOverwrites: Partial<ActionDescriptor> = {};
    if (isMultiSplitActive) {
      if (shiftKey && ctrlOrMetaKey) {
        multiSplitOverwrites.leftClick = "Add to Partition 2";
      } else if (ctrlOrMetaKey) {
        multiSplitOverwrites.leftClick = "Add to Partition 1";
      }
    }

    // --- TD-viewport viewport -----------------------------------------------------
    if (isTDViewportActive) {
      let maybeLeftClick: Partial<ActionDescriptor> = {};
      if (shiftKey) {
        maybeLeftClick.leftClick = "Jump to Point";
      } else if (ctrlOrMetaKey) {
        maybeLeftClick.leftClick = "Activate Supervoxel";
      }

      return {
        ...maybeLeftClick,
        ...multiSplitOverwrites,
        leftDrag: "Move",
        rightClick: "Context Menu",
      };
    }

    // --- Default ortho viewports -------------------------------------------
    let leftClick = "Select Segment to Proofread";
    if (shiftKey) {
      leftClick = "Merge with active Segment";
    } else if (ctrlOrMetaKey) {
      leftClick = "Split from active Segment";
    }

    return {
      leftClick,
      ...multiSplitOverwrites,
      rightClick: "Context Menu",
    };
  }

  static onToolDeselected() {}
}

const toolToToolController = {
  [AnnotationTool.MOVE.id]: MoveToolController,
  [AnnotationTool.SKELETON.id]: SkeletonToolController,
  [AnnotationTool.BOUNDING_BOX.id]: BoundingBoxToolController,
  [AnnotationTool.QUICK_SELECT.id]: QuickSelectToolController,
  [AnnotationTool.PROOFREAD.id]: ProofreadToolController,
  [AnnotationTool.BRUSH.id]: DrawToolController,
  [AnnotationTool.TRACE.id]: DrawToolController,
  [AnnotationTool.ERASE_TRACE.id]: EraseToolController,
  [AnnotationTool.ERASE_BRUSH.id]: EraseToolController,
  [AnnotationTool.FILL_CELL.id]: FillCellToolController,
  [AnnotationTool.VOXEL_PIPETTE.id]: VoxelPipetteToolController,
  [AnnotationTool.LINE_MEASUREMENT.id]: LineMeasurementToolController,
  [AnnotationTool.AREA_MEASUREMENT.id]: AreaMeasurementToolController,
};
export function getToolControllerForAnnotationTool(activeTool: AnnotationTool) {
  return toolToToolController[activeTool.id];
}

export const AllToolKeyboardControls = Object.fromEntries(
  Object.entries(toolToToolController).map(([id, controller]) => [
    id,
    controller.getKeyboardControls(),
  ]),
) as Record<AnnotationToolId, Partial<KeyboardShortcutHandlerMap>>;
